// Axios config file
import axios from "axios";

export const TOKEN_STORAGE_KEY = "admin_token";

export function getAdminToken() {
  return localStorage.getItem(TOKEN_STORAGE_KEY) || "";
}

export function setAdminToken(token) {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

export function clearAdminToken() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

const api = axios.create({
  // Same-origin /api in production (Firebase Hosting rewrite); override with
  // VUE_APP_FUNCTION_HOST to point the dev server at the emulator.
  baseURL: process.env.VUE_APP_FUNCTION_HOST || "/api",
  timeout: 15000,
});

api.interceptors.request.use((config) => {
  const token = getAdminToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      clearAdminToken();
      if (window.location.pathname !== "/login") {
        window.location.assign("/login");
      }
    }
    if (error.code === "ECONNABORTED" || error.message.includes("aborted")) {
      console.warn("Request was aborted or timed out");
      return Promise.reject({
        ...error,
        isAborted: true,
        message: "Request timed out or was cancelled",
      });
    }
    return Promise.reject(error);
  },
);

// Retry utility for handling temporary failures
const retryRequest = async (requestFn, maxRetries = 2, delay = 1000) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      if (attempt === maxRetries || error.isAborted) {
        throw error;
      }
      console.warn(
        `Request failed (attempt ${attempt}/${maxRetries}), retrying in ${delay}ms...`,
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
      delay *= 1.5; // Exponential backoff
    }
  }
};

export const getCampaignById = async function (campaignId) {
  return retryRequest(async () => {
    const response = await api.get("/campaign", { params: { campaignId } });
    return response.data.campaign;
  });
};

export const getCampaignByName = async function (campaignName) {
  const response = await api.get("/campaign", { params: { campaignName } });
  return response.data;
};

// Returns the full saved campaign (including server-assigned strategy _ids,
// needed to build the /track links). Throws on failure.
export const saveCampaign = async function (campaign) {
  const response = await api.post("/campaign", { campaign });
  return response.data.campaign;
};

export const getEnums = async function () {
  const response = await api.get("/enums");
  const { ContentTypeEnum, CampaignEnum } = response.data;
  return {
    contentTypeEnum: ContentTypeEnum,
    campaignEnum: CampaignEnum,
  };
};

export const getAllCampaigns = async function () {
  try {
    const response = await api.get("/campaign");
    return response.data;
  } catch (error) {
    if (error.isAborted) {
      console.warn("Campaigns request was aborted:", error.message);
    } else {
      console.error("Error fetching campaigns:", error);
    }
    return { campaigns: [] };
  }
};

export const addStrategyToCampaign = async function (campaignId, strategy) {
  const response = await api.put(`/campaign/${campaignId}/strategy`, {
    strategy,
  });
  return response.data;
};

export const getTrafficCount = async function (
  campaignId,
  { strategyType = null, strategyId = null } = {},
) {
  const params = { campaignId };
  if (strategyId) {
    params.strategyId = strategyId;
  } else if (strategyType) {
    params.strategyType = strategyType;
  }

  try {
    const response = await api.get("/traffic/count", { params });
    return response.data.count || 0;
  } catch (error) {
    console.error("Error fetching traffic count:", error);
    return 0;
  }
};

// Used by the login page to verify a token before storing it
export const verifyToken = async function (token) {
  const response = await api.get("/campaign", {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.status === 200;
};
